import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Loader2, Image, Copy, Check, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMedia = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${API}/admin/media`, { headers });
      setFiles(res.data);
    } catch (err) {
      toast.error('Error al cargar archivos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFiles(); }, []);

  const handleUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;

    setUploading(true);
    let uploaded = 0;
    for (const file of selectedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        await axios.post(`${API}/admin/media/upload`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        uploaded++;
      } catch (err) {
        const msg = err.response?.data?.detail || 'Error al subir archivo';
        toast.error(`${file.name}: ${msg}`);
      }
    }
    if (uploaded > 0) {
      toast.success(`${uploaded} archivo(s) subido(s)`);
      fetchFiles();
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (fileId, filename) => {
    if (!window.confirm(`¿Eliminar "${filename}"?`)) return;
    try {
      await axios.delete(`${API}/admin/media/${fileId}`, { headers });
      toast.success('Archivo eliminado');
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const copyUrl = (fileId) => {
    const url = `${API}/media/file/${fileId}`;
    navigator.clipboard.writeText(url);
    setCopiedId(fileId);
    toast.success('URL copiada');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filtered = files.filter((f) =>
    f.original_filename?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-8" data-testid="admin-media-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1917]">Media Library</h1>
          <p className="text-[#78716C] text-sm mt-1">{files.length} archivo(s)</p>
        </div>
        <div className="flex gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            onChange={handleUpload}
            className="hidden"
            data-testid="media-file-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-[#C2410C] hover:bg-[#9A3412]"
            data-testid="media-upload-btn"
          >
            {uploading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Subiendo...</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" /> Subir imagenes</>
            )}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A8A29E]" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar archivos..."
          className="pl-10"
          data-testid="media-search-input"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#C2410C]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-[#78716C]">
          <Image className="h-16 w-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">No hay archivos</p>
          <p className="text-sm mt-1">Sube imagenes para tu sitio web</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filtered.map((file) => (
            <div
              key={file.id}
              className="group bg-white rounded-xl border border-[#E7E5E4] overflow-hidden hover:shadow-md transition-shadow"
              data-testid={`media-item-${file.id}`}
            >
              <div className="aspect-square bg-[#F5F3EF] relative overflow-hidden">
                <img
                  src={`${API}/media/file/${file.id}`}
                  alt={file.original_filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                  <button
                    onClick={() => copyUrl(file.id)}
                    className="p-2 bg-white rounded-full shadow-md hover:bg-[#F5F3EF]"
                    title="Copiar URL"
                    data-testid={`media-copy-${file.id}`}
                  >
                    {copiedId === file.id ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4 text-[#1C1917]" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDelete(file.id, file.original_filename)}
                    className="p-2 bg-white rounded-full shadow-md hover:bg-red-50"
                    title="Eliminar"
                    data-testid={`media-delete-${file.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </button>
                </div>
              </div>
              <div className="p-3">
                <p className="text-sm font-medium text-[#1C1917] truncate">
                  {file.original_filename}
                </p>
                <p className="text-xs text-[#A8A29E] mt-0.5">
                  {formatSize(file.size)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMedia;
